// Where the reader was headed when the session ran out. Copied verbatim
// (pattern + reasoning) from admin's `lib/return-to.ts` — a bookmarked
// dashboard link (`/d/<slug>`) with an expired session must survive the
// Google round trip, since the callback can only redirect to the BI app
// ROOT (a client-side route never reaches the backend).
//
// Three properties keep a remembered link from becoming a trap:
//   - **sessionStorage, not localStorage.** The return belongs to THIS tab's
//     round trip to Google. A second tab must not inherit it, and closing the
//     browser must drop it.
//   - **It expires**, on the same 10-minute clock as the backend's PKCE/state
//     cookies (`web/routes/bi_auth.py`), which bound the same round trip.
//   - **It is consumed on read, and only at the bare home path** — the exact
//     thing the OAuth callback redirects to. An abandoned sign-in therefore
//     costs one redirect, once, and never hijacks a later visit.
//
// Only a same-origin absolute path is ever stored or restored
// (`sanitizeReturnTo`): this writes the address bar, so anything else is an
// open redirect.

const STORAGE_KEY = "recruiter_bi_app_return_to";

/** Mirrors the 600s `max_age` on `web/routes/bi_auth.py`'s PKCE + state
 *  cookies: past it, the sign-in round trip this link belonged to can no
 *  longer complete anyway. */
const MAX_AGE_MS = 10 * 60 * 1000;

/** Refuse to store anything longer than a plausible deep link. */
const MAX_PATH_LENGTH = 2048;

interface StoredReturnTo {
  readonly path: string;
  /** Epoch ms, so the freshness check needs no parsing. */
  readonly savedAt: number;
}

/** The slice of `window` this module touches, named so tests can pass a fake. */
export interface ReturnToWindow {
  readonly location: { readonly pathname: string; readonly search: string; readonly hash: string };
  readonly history: { replaceState(data: unknown, unused: string, url: string): void };
}

function safeSessionStorage(): Storage | undefined {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A candidate return path → the path we are willing to put in the address bar,
 * or null. Accepts ONLY a same-origin absolute path — see admin's twin module
 * for the exact open-redirect reasoning this mirrors.
 */
export function sanitizeReturnTo(raw: string): string | null {
  const path = raw.trim();
  if (path.length === 0 || path.length > MAX_PATH_LENGTH) return null;
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//") || path.startsWith("/\\")) return null;
  for (const ch of path) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0x20 || code === 0x7f) return null;
  }
  if (path === "/") return null;
  return path;
}

/** True for the page the OAuth callback lands on: the bare BI root, with no
 *  query and no fragment of its own. */
function isBareHome(location: ReturnToWindow["location"]): boolean {
  return (
    (location.pathname === "/" || location.pathname === "") &&
    location.search === "" &&
    location.hash === ""
  );
}

/** Remember where we are, on the way out to Google. A no-op for Home (nothing
 *  to return to) and when storage is unavailable (private mode). */
export function rememberReturnTo(
  location: ReturnToWindow["location"],
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): void {
  const path = sanitizeReturnTo(`${location.pathname}${location.search}${location.hash}`);
  if (!storage) return;
  try {
    if (!path) {
      storage.removeItem(STORAGE_KEY);
      return;
    }
    const entry: StoredReturnTo = { path, savedAt: now.getTime() };
    storage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota / private mode — the link is lost, the sign-in still works.
  }
}

/** Forget any remembered link (sign out). */
export function clearReturnTo(storage: Storage | undefined = safeSessionStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // no-op — see rememberReturnTo.
  }
}

/**
 * Take the remembered link, if one applies here. Always consumes: a link that
 * is stale, malformed, or arrives somewhere other than Home is dropped rather
 * than left to fire on the next visit.
 */
export function consumeReturnTo(
  location: ReturnToWindow["location"],
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): string | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
    if (raw !== null) storage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null || !isBareHome(location)) return null;

  let entry: unknown;
  try {
    entry = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof entry !== "object" || entry === null) return null;
  const { path, savedAt } = entry as Partial<StoredReturnTo>;
  if (typeof path !== "string" || typeof savedAt !== "number" || !Number.isFinite(savedAt)) {
    return null;
  }
  const age = now.getTime() - savedAt;
  if (age < 0 || age > MAX_AGE_MS) return null;
  return sanitizeReturnTo(path);
}

/**
 * Put the address bar back on the deep link the reader was signing in for.
 * Called from `main.tsx` BEFORE React renders. Returns the restored path, or
 * null when there was nothing to restore.
 */
export function restoreReturnTo(
  win: ReturnToWindow = window,
  storage: Storage | undefined = safeSessionStorage(),
  now: Date = new Date(),
): string | null {
  const path = consumeReturnTo(win.location, storage, now);
  if (path) win.history.replaceState(null, "", path);
  return path;
}
